import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGalleryPhotos, getGalleryPhotosBySuiteId } from '../../data/gallery';
import { getAllApartments } from '../../data/apartments';
import { trackEvent } from '../../services/analyticsService';
import { applyImageFallback } from '../../utils/imageFallback';
import type { GalleryPhoto } from '../../types';

// Unified filter key: 'all' | 'suite-N' | 'stavros' | 'ithaca'
type FilterKey = 'all' | `suite-${number}` | GalleryPhoto['category'];

export default function GallerySection() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [lightbox, setLightbox] = useState<number | null>(null);
  // Bump key so grid re-mounts (retriggers tile animation) when filter changes
  const [gridKey, setGridKey] = useState(0);

  const apartments = useMemo(() => getAllApartments(), []);

  const photos = useMemo(() => {
    if (filter === 'all') return getGalleryPhotos();
    if (filter.startsWith('suite-')) {
      const id = Number.parseInt(filter.replace('suite-', ''), 10);
      return getGalleryPhotosBySuiteId(id);
    }
    return getGalleryPhotos(filter as GalleryPhoto['category']);
  }, [filter]);

  // Reset lightbox and trigger grid animation when filter changes
  const prevFilter = useRef(filter);
  useEffect(() => {
    if (prevFilter.current !== filter) {
      prevFilter.current = filter;
      setLightbox(null);
      setGridKey((k) => k + 1);
    }
  }, [filter]);

  // Listen for suite selection from the Suites section
  useEffect(() => {
    const onSelect = (e: Event) => {
      const id = (e as CustomEvent<{ id: number }>).detail?.id;
      if (id) setFilter(`suite-${id}`);
    };
    globalThis.addEventListener('villaspilia:selectSuite', onSelect);
    return () => globalThis.removeEventListener('villaspilia:selectSuite', onSelect);
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const next = useCallback(() => {
    setLightbox((i) => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);
  const prev = useCallback(() => {
    setLightbox((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  // Keyboard navigation
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    globalThis.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      globalThis.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, closeLightbox, next, prev]);

  // Touch swipe
  useEffect(() => {
    if (lightbox === null) return;
    let startX = 0;
    const onStart = (e: TouchEvent) => { startX = e.touches[0]?.clientX ?? 0; };
    const onEnd = (e: TouchEvent) => {
      const dx = (e.changedTouches[0]?.clientX ?? 0) - startX;
      if (Math.abs(dx) > 60) (dx < 0 ? next : prev)();
    };
    globalThis.addEventListener('touchstart', onStart, { passive: true });
    globalThis.addEventListener('touchend', onEnd);
    return () => {
      globalThis.removeEventListener('touchstart', onStart);
      globalThis.removeEventListener('touchend', onEnd);
    };
  }, [lightbox, next, prev]);

  const current = lightbox === null ? null : photos[lightbox];

  // Build chip list: All, Suite 1/2/3, Stavros, Ithaca
  const scenicCategories: { key: FilterKey; labelKey: string; count: number }[] = [
    // { key: 'stavros', labelKey: 'gallery.categories.stavros', count: getGalleryPhotos('stavros').length },
    { key: 'ithaca', labelKey: 'gallery.categories.ithaca', count: getGalleryPhotos('ithaca').length },
  ];

  return (
    <section id="gallery" className="gallery section section--soft">
      <div className="container">
        <header className="section-head section-head--center">
          <p className="eyebrow eyebrow--center reveal">{t('gallery.eyebrow')}</p>
          <h2 className="reveal">{t('gallery.title')}</h2>
          <p className="lede reveal">{t('gallery.subtitle')}</p>
        </header>

        {/* Filter chips */}
        <div className="gallery__filters reveal" role="tablist">
          {/* All */}
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all'}
            className={`filter-chip${filter === 'all' ? ' is-active' : ''}`}
            onClick={() => {
              setFilter('all');
              trackEvent('select_content', {
                content_type: 'gallery_filter',
                content_id: 'all',
                section_id: 'gallery',
              });
            }}
          >
            {t('gallery.categories.all')}
            <span className="count">{getGalleryPhotos().length}</span>
          </button>

          {/* Suite chips */}
          {apartments.map((apt) => {
            const key: FilterKey = `suite-${apt.id}`;
            const name = t(`apartments.suites.${apt.id}.name`, { defaultValue: apt.name });
            const count = getGalleryPhotosBySuiteId(apt.id).length;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                className={`filter-chip filter-chip--suite${filter === key ? ' is-active' : ''}`}
                onClick={() => {
                  setFilter(key);
                  trackEvent('select_content', {
                    content_type: 'gallery_filter',
                    content_id: `suite_${apt.id}`,
                    section_id: 'gallery',
                  });
                }}
              >
                {name}
                <span className="count">{count}</span>
              </button>
            );
          })}

          {/* Scenic chips */}
          {scenicCategories.map((c) => (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={filter === c.key}
              className={`filter-chip${filter === c.key ? ' is-active' : ''}`}
              onClick={() => {
                setFilter(c.key);
                trackEvent('select_content', {
                  content_type: 'gallery_filter',
                  content_id: c.key,
                  section_id: 'gallery',
                });
              }}
            >
              {t(c.labelKey)}
              <span className="count">{c.count}</span>
            </button>
          ))}
        </div>

        {/* Gallery grid — key resets CSS entrance animation on filter change */}
        <div key={gridKey} className="gallery-grid">
          {photos.map((p, idx) => {
            const tileKey = p.category === 'apartment' ? `${p.suiteId}-${p.id}` : String(p.id);
            return (
              <button
                key={tileKey}
                type="button"
                className="gallery-tile"
                style={{ '--tile-i': idx } as React.CSSProperties}
                onClick={() => {
                  setLightbox(idx);
                  trackEvent('view_item', {
                    item_id: `gallery_${p.id}`,
                    item_name: p.caption,
                    section_id: 'gallery',
                  });
                }}
                aria-label={p.caption}
              >
                <img
                  src={p.imageUrl}
                  alt={p.caption}
                  loading="lazy"
                  onError={(e) => applyImageFallback(e.currentTarget, p.caption)}
                />
                <span className="gallery-tile__caption">{p.caption}</span>
              </button>
            );
          })}
        </div>
      </div>

      {current && (
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
        <dialog
          className="lightbox"
          aria-label={current.caption}
          open
          onClick={closeLightbox}
          onKeyDown={(e) => { if (e.key === 'Escape') closeLightbox(); }}
        >
          <button type="button" className="lightbox__close" onClick={closeLightbox} aria-label={t('common.close')}>
            ×
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label={t('common.previous')}
          >
            ‹
          </button>
          <div className="lightbox__stage">
            <button
              type="button"
              className="lightbox__stage-inner"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={current.caption}
              tabIndex={-1}
            >
              <img src={current.imageUrl} alt={current.caption} />
            </button>
            <p className="lightbox__caption">{current.caption}</p>
            <p className="lightbox__counter">{(lightbox ?? 0) + 1} / {photos.length}</p>
          </div>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label={t('common.next')}
          >
            ›
          </button>
        </dialog>
      )}
    </section>
  );
}

